import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  CreditCard, 
  DollarSign,
  Calendar,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { userDetailService, UserSubscriptionDetail } from '@/services/admin/user-detail-service';
import { formatDistanceToNow, format } from 'date-fns';
import { toast } from 'sonner';

interface UserSubscriptionBillingTabProps {
  userId: string;
}

export const UserSubscriptionBillingTab: React.FC<UserSubscriptionBillingTabProps> = ({ userId }) => {
  const [subscriptions, setSubscriptions] = useState<UserSubscriptionDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    try {
      const subscriptionData = await userDetailService.getUserSubscriptionDetails(userId);
      setSubscriptions(subscriptionData);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const getSubscriptionStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </Badge>
        );
      case 'expired':
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Expired
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="secondary">
            <AlertCircle className="h-3 w-3 mr-1" />
            Cancelled
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {status}
          </Badge>
        );
    }
  };

  const getPaymentStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Paid
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
            <AlertCircle className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {status}
          </Badge>
        );
    }
  };

  const getPlanBadge = (packageName: string) => {
    if (packageName.toLowerCase().includes('enterprise')) {
      return <Badge className="bg-purple-100 text-purple-800 border-purple-200">Enterprise</Badge>;
    }
    if (packageName.toLowerCase().includes('premium')) {
      return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Premium</Badge>;
    }
    return <Badge variant="secondary">Basic</Badge>;
  };

  const currentSubscription = subscriptions.find(sub => sub.status === 'active');
  const totalSpent = subscriptions
    .filter(sub => sub.paymentStatus === 'paid')
    .reduce((sum, sub) => sum + sub.packagePrice, 0);

  if (isLoading) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-16" />
                  <Skeleton className="h-16" />
                </div>
                <Skeleton className="h-24 w-full" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Billing Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Current Plan</p>
                <p className="text-lg font-bold">{currentSubscription?.packageName || 'Free'}</p>
              </div>
              <CreditCard className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Spent</p>
                <p className="text-2xl font-bold">${totalSpent.toLocaleString()}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Subscriptions</p>
                <p className="text-2xl font-bold">{subscriptions.length}</p>
              </div>
              <RefreshCw className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Since</p>
                <p className="text-lg font-bold">
                  {currentSubscription 
                    ? formatDistanceToNow(new Date(currentSubscription.startDate), { addSuffix: true })
                    : 'N/A'
                  }
                </p>
              </div>
              <Calendar className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Current Subscription */}
      {currentSubscription && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Current Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Plan</span>
                  <div className="flex items-center gap-2">
                    {getPlanBadge(currentSubscription.packageName)}
                    <span className="font-medium">{currentSubscription.packageName}</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Status</span>
                  {getSubscriptionStatusBadge(currentSubscription.status)}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Billing Cycle</span>
                  <span className="font-medium capitalize">{currentSubscription.renewalType}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Payment Method</span>
                  <span className="font-medium capitalize">{currentSubscription.paymentMethod}</span>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Start Date</span>
                  <span className="font-medium">{format(new Date(currentSubscription.startDate), 'PPP')}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">End Date</span>
                  <span className="font-medium">{format(new Date(currentSubscription.endDate), 'PPP')}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Price</span>
                  <span className="text-lg font-bold">${currentSubscription.packagePrice}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Payment Status</span>
                  {getPaymentStatusBadge(currentSubscription.paymentStatus)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Subscription History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {subscriptions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CreditCard className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>No subscription history found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {subscriptions.map((subscription) => (
                <div key={subscription.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-medium">{subscription.packageName}</h3>
                      {getPlanBadge(subscription.packageName)}
                      {getSubscriptionStatusBadge(subscription.status)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <p>
                        {format(new Date(subscription.startDate), 'PPP')} - {format(new Date(subscription.endDate), 'PPP')}
                      </p>
                      <p>
                        Payment: {subscription.paymentMethod} • {subscription.renewalType}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-lg font-bold">${subscription.packagePrice}</div>
                    {getPaymentStatusBadge(subscription.paymentStatus)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Billing Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Billing Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button variant="outline" className="justify-start">
              <CreditCard className="h-4 w-4 mr-2" />
              Update Plan
            </Button>
            
            <Button variant="outline" className="justify-start">
              <Calendar className="h-4 w-4 mr-2" />
              Extend Subscription
            </Button>
            
            <Button variant="outline" className="justify-start">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Billing
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};